import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  listGiveawayEntries,
  updateGiveawayEntry,
  exportGiveawayEntries,
} from "../services/giveawayService";
import { apiProblem, ENTRY_STATUS_LABELS, fmtEt, showProblem, toastOk } from "./giveawayUtils";
import { ActionModal, EntryPill } from "./GiveawayUi";

// Everyone who entered. Emails arrive masked; showing them in full is logged
// on the server, so it sits behind a confirm that says so.

const NOTE_NEEDED = ["duplicate", "disqualified"];

const GiveawayEntriesTab = ({ g, reload, onUnauthorized }) => {
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [status, setStatus] = useState("");
  const [tests, setTests] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);

  // Wait for typing to pause before searching.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qLive.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [qLive]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { page, limit };
      if (q) params.q = q;
      if (status) params.status = status;
      if (reveal) params.reveal = 1;
      if (tests) params.tests = 1;
      const res = await listGiveawayEntries(g._id, params);
      const d = res.data?.data || {};
      setData({ rows: Array.isArray(d.rows) ? d.rows : [], total: d.total || 0 });
    } catch (e) {
      if (e?.response?.status === 401) return onUnauthorized();
      setErr(apiProblem(e, "Could not load the entries.").message);
    } finally {
      setLoading(false);
    }
  }, [g._id, q, status, reveal, tests, page, limit, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const after = async (fn, okText) => {
    try {
      await fn();
      toastOk(okText);
      await Promise.all([load(), reload()]);
      return true;
    } catch (e) {
      if (e?.response?.status === 401) {
        onUnauthorized();
        return true;
      }
      showProblem(apiProblem(e));
      return false;
    }
  };

  const askReveal = () => {
    if (reveal) return setReveal(false);
    setModal({
      title: "Show full email addresses?",
      body:
        "Full emails are personal information. Showing them is recorded in this giveaway's audit trail with your name and the time.",
      confirmLabel: "Show full emails",
      onConfirm: () => {
        setReveal(true);
        return true;
      },
    });
  };

  const askStatus = (row, to) => {
    if (!to || to === row.status) return;
    const needNote = NOTE_NEEDED.includes(to);
    setModal({
      title: `Mark as ${ENTRY_STATUS_LABELS[to].toLowerCase()}?`,
      body: `${row.firstName || ""} ${row.lastName || ""} (${row.email}) will be marked "${ENTRY_STATUS_LABELS[to]}".${
        to === "valid" ? " Valid entries are included in the draw." : " Only valid entries are included in the draw."
      }`,
      confirmLabel: "Save",
      fields: [
        {
          name: "note",
          label: needNote ? "Why?" : "Note (optional)",
          type: "textarea",
          required: needNote,
        },
      ],
      onConfirm: (v) =>
        after(
          () => updateGiveawayEntry(g._id, row._id, { status: to, note: v.note?.trim() || undefined }),
          "Entry updated"
        ),
    });
  };

  const askLink = (row) =>
    setModal({
      title: "Link to a Curiouzz member",
      body:
        "Only do this after checking the member and the entrant are the same person (same name and email). It helps count which entrants joined the app.",
      confirmLabel: "Link",
      fields: [
        { name: "linkedUserId", label: "Member ID", type: "text", required: true, initial: row.linkedUserId || "" },
        { name: "note", label: "How did you check it is the same person?", type: "textarea", required: true },
      ],
      onConfirm: (v) =>
        after(
          () => updateGiveawayEntry(g._id, row._id, { linkedUserId: v.linkedUserId.trim(), note: v.note.trim() }),
          "Entry linked to member"
        ),
    });

  const askExport = () =>
    setModal({
      title: "Download entries as a spreadsheet?",
      body: (
        <>
          <p className="mb-8">
            This file contains personal information. Store it securely and delete it when done.
          </p>
          <p className="mb-0">
            It includes {status ? `only "${ENTRY_STATUS_LABELS[status]}" entries` : "valid entries only"}. The
            download is recorded in the audit trail.
          </p>
        </>
      ),
      confirmLabel: "Download",
      onConfirm: async () => {
        try {
          const res = await exportGiveawayEntries(g._id, { status: status || "valid" });
          const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv" });
          const cd = res.headers?.["content-disposition"] || "";
          const m = /filename="?([^";]+)"?/i.exec(cd);
          const name = m ? m[1] : `${g.slug}-${status || "valid"}-entries.csv`;
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          toastOk("Download started");
          return true;
        } catch (e) {
          if (e?.response?.status === 401) {
            onUnauthorized();
            return true;
          }
          // The error body is a blob too; read the server's message out of it.
          let p = apiProblem(e, "Could not download the entries.");
          try {
            if (e?.response?.data instanceof Blob) {
              const j = JSON.parse(await e.response.data.text());
              p = { ...p, message: j.message || p.message, list: [...(j.blockers || []), ...(j.reasons || [])] };
            }
          } catch {
            /* keep the fallback */
          }
          showProblem(p);
          return false;
        }
      },
    });

  const pages = Math.max(1, Math.ceil(data.total / limit));
  const from = data.total ? (page - 1) * limit + 1 : 0;
  const to = Math.min(page * limit, data.total);

  return (
    <div className="card radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 cz-toolbar">
        <div className="cz-topline">
          <form className="navbar-search cz-search" style={{ position: "relative" }} onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              placeholder="Search name or email"
              value={qLive}
              onChange={(e) => setQLive(e.target.value)}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
          <div className="cz-actions">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={askReveal}>
              <Icon icon={reveal ? "mdi:eye-off-outline" : "mdi:eye-outline"} className="me-1" />
              {reveal ? "Hide full emails" : "Show full emails"}
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={askExport}>
              <Icon icon="mdi:download" className="me-1" /> Export
            </button>
          </div>
        </div>
        <div className="cz-filters">
          <select
            className="form-select"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            {Object.entries(ENTRY_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <label className="d-flex align-items-center gap-2 text-sm mb-0" style={{ width: "auto" }}>
            <input
              type="checkbox"
              className="form-check-input mt-0"
              checked={tests}
              onChange={(e) => {
                setTests(e.target.checked);
                setPage(1);
              }}
            />
            Show test entries (from the preview link)
          </label>
        </div>
      </div>

      <div className="card-body p-24">
        {err ? <div className="alert alert-danger py-2">{err}</div> : null}
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>City</th>
                <th>Entered</th>
                <th>Status</th>
                <th>Why flagged</th>
                <th>Source</th>
                <th>News opt-in</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data.rows.length ? (
                <tr>
                  <td colSpan={10}>Loading…</td>
                </tr>
              ) : data.rows.length === 0 ? (
                <tr>
                  <td colSpan={10}>{q || status ? "No entries fit these filters." : "No entries yet."}</td>
                </tr>
              ) : (
                data.rows.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <div className="fw-semibold">
                        {r.firstName} {r.lastName}
                      </div>
                      {r.isTest ? <span className="text-xs" style={{ color: "#FA69B9" }}>Test entry</span> : null}
                      {r.linkedUserId ? (
                        <div className="text-xs">
                          <Link to={`/user/view-user/${r.linkedUserId}`} className="text-primary-600">
                            Curiouzz member
                          </Link>
                        </div>
                      ) : null}
                    </td>
                    <td className="text-sm">{r.email}</td>
                    <td className="text-sm">
                      {r.city || "Not given"}
                      {r.cityStatus === "unknown" ? (
                        <div className="text-xs" style={{ color: "#B45309" }}>
                          not on list
                        </div>
                      ) : null}
                    </td>
                    <td className="text-sm text-nowrap">{fmtEt(r.createdAt, "")}</td>
                    <td>
                      <EntryPill status={r.status} />
                      {r.statusNote ? (
                        <div className="text-xs text-secondary-light mt-1" style={{ maxWidth: 200 }}>
                          {r.statusNote}
                        </div>
                      ) : null}
                    </td>
                    <td className="text-xs" style={{ maxWidth: 220 }}>
                      {(r.reviewReasons || []).length ? (r.reviewReasons || []).join(", ") : "None"}
                    </td>
                    <td className="text-xs">
                      {r.entryMethod === "app_new" ? "App: new member" : r.entryMethod === "app_member" ? "App: existing member" : r.utm?.source || r.source || "Web form"}
                      {r.utm?.campaign ? <div className="text-secondary-light">{r.utm.campaign}</div> : null}
                    </td>
                    <td className="text-sm">{r.marketing?.optedIn ? "Yes" : "No"}</td>
                    <td className="text-xs">{r.confirmationEmail?.status || "Not sent"}</td>
                    <td>
                      <div className="d-flex flex-column gap-1" style={{ minWidth: 150 }}>
                        <select
                          className="form-select form-select-sm"
                          value=""
                          onChange={(e) => askStatus(r, e.target.value)}
                          aria-label="Change status"
                        >
                          <option value="">Change status…</option>
                          {Object.entries(ENTRY_STATUS_LABELS)
                            .filter(([k]) => k !== r.status)
                            .map(([k, v]) => (
                              <option key={k} value={k}>
                                {v}
                              </option>
                            ))}
                        </select>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => askLink(r)}>
                          {r.linkedUserId ? "Change member link" : "Link to member"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24 cz-footer">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary-light">Rows per page</span>
              <select
                className="cz-pagesize"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <span>
              Showing {from} to {to} of {data.total}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {pages}
            </span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ActionModal show={!!modal} {...(modal || {})} onClose={() => setModal(null)} />
    </div>
  );
};

export default GiveawayEntriesTab;
